import { ReactNode, useId } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

interface ChoiceCardProps {
  selected: boolean;
  onClick: () => void;
  label: ReactNode;
  description?: ReactNode;
  /** "radio" for one-of-many answers, "checkbox" for an independent yes/no choice. */
  kind?: "radio" | "checkbox";
  disabled?: boolean;
}

/**
 * A large tappable answer, in place of a bare radio or checkbox: the whole card is the target,
 * and the selected state is shown by the border, the tint AND the icon — never colour alone.
 * Exposes the matching ARIA role, so screen readers announce it as the control it behaves as.
 */
export default function ChoiceCard({ selected, onClick, label, description, kind = "radio", disabled }: ChoiceCardProps) {
  const labelId = useId();
  const descriptionId = useId();
  const Icon = kind === "radio"
    ? selected ? CheckCircleIcon : RadioButtonUncheckedIcon
    : selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

  return (
    <ButtonBase
      role={kind}
      aria-checked={selected}
      aria-labelledby={labelId}
      aria-describedby={description ? descriptionId : undefined}
      disabled={disabled}
      onClick={onClick}
      sx={(theme) => ({
        width: "100%",
        textAlign: "left",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        border: 2,
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
        transition: "border-color .15s, background-color .15s, box-shadow .15s",
        "&:hover": { borderColor: selected ? "primary.main" : "text.disabled", boxShadow: theme.shadows[1] },
        "&.Mui-focusVisible": { outline: `3px solid ${alpha(theme.palette.primary.main, 0.4)}`, outlineOffset: 2 },
        "&.Mui-disabled": { opacity: 0.55 },
      })}
    >
      <Icon sx={{ color: selected ? "primary.main" : "text.secondary", mt: "1px" }} aria-hidden />
      <Box>
        <Typography id={labelId} sx={{ fontWeight: 600 }}>{label}</Typography>
        {description && (
          <Typography id={descriptionId} variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}
